import { Injectable, computed, signal } from '@angular/core';

export type AuthorityCode = 'PDA' | 'LDA';
export type UserRole = 'officer' | 'config_admin' | 'verifier';

export interface SessionUser {
  userId: string;
  displayName: string;
  authorityId: AuthorityCode;
  role: UserRole;
}

interface StoredAuth {
  user: SessionUser;
  accessToken: string;
  expiresIn?: string;
}

const STORAGE_KEY = 'sbc.auth.v2';

export const DMS_ADMIN_ONLY_MESSAGE = 'Only admins are allowed for this service.';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly stored = signal<StoredAuth | null>(this.readStored());

  readonly loggedIn = computed(() => !!this.stored()?.accessToken);
  readonly accessToken = computed(() => this.stored()?.accessToken ?? '');
  readonly authorityId = computed(() => this.stored()?.user.authorityId ?? 'PDA');
  readonly role = computed(() => this.stored()?.user.role ?? 'officer');
  readonly userId = computed(() => this.stored()?.user.userId ?? '');
  readonly displayName = computed(() => this.stored()?.user.displayName ?? '');

  roleLabel(): string {
    const map: Record<UserRole, string> = {
      officer: 'Building Officer',
      config_admin: 'Admin',
      verifier: 'Plan Verifier',
    };
    return map[this.role()] || this.role();
  }

  /** Sidebar footer — short label without duplicate role text. */
  sidebarName(): string {
    if (this.role() === 'config_admin') return 'Admin';
    const name = this.displayName().trim();
    if (name) return name;
    return this.roleLabel();
  }

  homePath(): string {
    return '/enforcement/dashboard';
  }

  canAccessPlans(): boolean {
    return ['verifier', 'config_admin'].includes(this.role());
  }

  canManagePlans(): boolean {
    return ['verifier', 'config_admin'].includes(this.role());
  }

  canAccessScans(): boolean {
    return ['officer', 'config_admin'].includes(this.role());
  }

  canAccessMeasurements(): boolean {
    return ['officer', 'config_admin'].includes(this.role());
  }

  canAccessFlags(): boolean {
    return ['officer', 'config_admin'].includes(this.role());
  }

  canAccessConfig(): boolean {
    return this.role() === 'config_admin';
  }

  /** DMS workspace is admin-only. */
  canAccessDms(): boolean {
    return this.role() === 'config_admin';
  }

  /** View bye-law reference table (officers read-only; admin can edit). */
  canAccessByeLaws(): boolean {
    return ['officer', 'config_admin'].includes(this.role());
  }

  authorityLabel(): string {
    return this.authorityId() === 'LDA' ? 'Lucknow (LDA)' : 'Prayagraj (PDA)';
  }

  login(input: {
    userId: string;
    displayName?: string;
    authorityId: AuthorityCode;
    role: UserRole;
    accessToken: string;
    expiresIn?: string;
  }): void {
    const userId = input.userId.trim() || 'demo-user';
    const next: StoredAuth = {
      user: {
        userId,
        displayName: (input.displayName || '').trim() || userId,
        authorityId: input.authorityId,
        role: input.role,
      },
      accessToken: input.accessToken,
      expiresIn: input.expiresIn,
    };
    this.stored.set(next);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }

  logout(): void {
    this.stored.set(null);
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('sbc.session.v1');
  }

  /** Legacy header stub — JWT is sent via authInterceptor. */
  headers(options?: { json?: boolean }): Record<string, string> {
    const h: Record<string, string> = {};
    if (options?.json !== false) {
      h['Content-Type'] = 'application/json';
    }
    return h;
  }

  private readStored(): StoredAuth | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredAuth;
      if (!parsed?.user?.userId || !parsed?.user?.authorityId || !parsed?.user?.role) {
        return null;
      }
      if (!parsed.accessToken) return null;
      return parsed;
    } catch {
      return null;
    }
  }
}
